/**
 * Gradient Hero
 * 
 * Gradient/color background without image.
 * Clean and modern look for minimalist sites.
 */

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight, Sparkles } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const HeroGradient = () => {
  const { features } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
    small: 'min-h-[40vh]',
  };

  const alignmentClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  return (
    <section className={cn(
      "relative flex items-center justify-center overflow-hidden",
      heightClasses[heroConfig.height]
    )}>
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-heritage/80 to-culture/60" />
      
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-heritage/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-culture/10 rounded-full blur-2xl" />
      </div>

      {/* Animated Sparkles */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(6)].map((_, i) => (
          <Sparkles 
            key={i}
            className={cn(
              "absolute text-white/40 animate-pulse",
              i === 0 && "top-[20%] left-[10%] w-6 h-6",
              i === 1 && "top-[30%] right-[15%] w-8 h-8",
              i === 2 && "bottom-[25%] left-[20%] w-5 h-5",
              i === 3 && "top-[60%] right-[25%] w-7 h-7",
              i === 4 && "bottom-[40%] right-[10%] w-4 h-4",
              i === 5 && "top-[15%] left-[50%] w-6 h-6"
            )}
            style={{ animationDelay: `${i * 0.5}s` }}
          />
        ))}
      </div>

      {/* Content */}
      <div className={cn(
        "relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col",
        alignmentClasses[heroConfig.contentAlignment]
      )}>
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {hero.title}
            <span className="block text-accent mt-2">
              {hero.highlightedText}
            </span>
          </h1>
          
          <div className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            <RichTextDisplay content={hero.subtitle || ''} prose={false} />
          </div>

          {/* CTA Buttons */}
          {heroConfig.showCTA && (
            <div className={cn(
              "flex flex-col sm:flex-row gap-4",
              heroConfig.contentAlignment === 'center' && "justify-center",
              heroConfig.contentAlignment === 'right' && "justify-end"
            )}>
              {features.membership.enabled && (
                <Button 
                  variant="secondary" 
                  size="lg" 
                  asChild 
                  className="group bg-white text-primary hover:bg-white/90"
                >
                  <Link to="/membership">
                    <Users className="w-5 h-5" />
                    Join Our Community
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
              
              {features.donation.enabled && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  asChild 
                  className="group border-white text-white hover:bg-white/10"
                >
                  <Link to="/donate">
                    <Heart className="w-5 h-5" />
                    Support Us
                  </Link>
                </Button>
              )}
              
              {features.events.enabled && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  asChild 
                  className="group border-white text-white hover:bg-white/10"
                >
                  <Link to="/events">
                    <Calendar className="w-5 h-5" />
                    View Events
                  </Link>
                </Button>
              )}

              {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
                <Button 
                  variant="secondary" 
                  size="lg" 
                  asChild 
                  className="group bg-white text-primary hover:bg-white/90"
                >
                    <Link to="/about-us">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
            </div>
          )}

          {/* Statistics */}
          {heroConfig.showStats && hero.showStats && hero.stats.length > 0 && (
            <div className={cn(
              "mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8",
              heroConfig.contentAlignment === 'center' && "text-center"
            )}>
              {hero.stats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg 
          viewBox="0 0 1440 120" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path 
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
            className="fill-background"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroGradient;




